import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Form,
  Input,
  List,
  Modal,
  Progress,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  InboxOutlined,
  ReloadOutlined,
  RobotOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../AuthProvider';
import FetchAllEmployeesByCompany from '../services/FetchAllEmployeesByCompany';
import {
  confirmDocumentUpload,
  fetchEmployeeWorkPreferences,
  fetchOnboardingStatus,
  markWelcomeSeen,
  requestPresignedUrl,
  reviewDocument,
  updateChecklistStep,
  updateOnboardingStatus,
} from '../services/OnboardingService';
import OnboardingManagerActions from './OnboardingManagerActions';
import OnboardingWelcomeCard from './OnboardingWelcomeCard';
import './Onboarding.scss';

const { Title, Text, Paragraph } = Typography;
const { Dragger } = Upload;

const documentTypeOptions = [
  { value: 'identity', label: 'Identity' },
  { value: 'tax_file_number', label: 'Tax file number' },
  { value: 'bank_details', label: 'Bank details' },
  { value: 'superannuation', label: 'Superannuation' },
  { value: 'right_to_work', label: 'Right to work' },
  { value: 'qualifications', label: 'Qualifications' },
  { value: 'other', label: 'Other' },
];

const statusColor = {
  pending: 'default',
  in_progress: 'processing',
  completed: 'success',
  skipped: 'warning',
  uploaded: 'processing',
  approved: 'success',
  rejected: 'error',
  under_review: 'warning',
  expired: 'error',
  pending_approval: 'warning',
};

function getCurrentEmployeeId(authContext) {
  const info = authContext.additionalUserInfo || {};
  return info.id || info.userId || info.employeeId || info.staffId || authContext.user?.userId || authContext.user?.username;
}

function getIsManager(role) {
  return ['ROLE_MANAGER', 'ROLE_CMANAGER', 'ROLE_FMANAGER', 'ROLE_SADMIN'].includes(role);
}

function hasBankDetails(profile) {
  const primaryBank = profile?.bankDetails?.[0];
  return Boolean(primaryBank?.bsbNumber && primaryBank?.accountNumber);
}

function hasBasicProfileDetails(profile) {
  return Boolean(profile?.firstName && profile?.lastName && profile?.email && (profile?.phoneNumber || profile?.phone || profile?.mobile));
}

function hasTaxDetails(profile) {
  return Boolean(profile?.taxFileNumber && (profile?.taxWithholdingStatus || profile?.residencyStatus));
}

function hasSuperannuationDetails(profile) {
  const superannuation = profile?.superannuation;
  return Boolean(
    superannuation?.superannuationName &&
    (superannuation?.superannuationMembrshipAccountNumber || superannuation?.superannuationFundABNNumber)
  );
}

function hasEmploymentConditions(profile) {
  return Boolean(
    profile?.employmentType &&
    profile?.timesheetRequiredForAgreedOrNormalHours &&
    profile?.resident &&
    profile?.helpDebt &&
    profile?.tfthreshold
  );
}

function hasWorkPreferences(workPreferences) {
  if (!workPreferences) {
    return false;
  }

  if (workPreferences.preferenceType === 'Ongoing') {
    return Array.isArray(workPreferences.availability) && workPreferences.availability.length > 0;
  }

  return Boolean(workPreferences.preferenceType && workPreferences.preferenceType !== 'Nopreference');
}

function getDocumentLinks(document, generatedLinkMap) {
  const images = Array.isArray(document?.documentImages) ? document.documentImages : [];

  if (images.length > 0) {
    return images
      .map((image) => ({
        label: image?.description || image?.keyName || image?.photoId || 'File',
        href: image?.presignedUrl || image?.s3Url || generatedLinkMap?.[image?.keyName],
      }))
      .filter((item) => !!item.href);
  }

  if (document?.presignedUrl) {
    return [{ label: document.documentName || 'File', href: document.presignedUrl }];
  }

  if (document?.s3Key && generatedLinkMap?.[document.s3Key]) {
    return [{ label: document.documentName || document.s3Key, href: generatedLinkMap[document.s3Key] }];
  }

  if (document?.documentName && generatedLinkMap?.[document.documentName]) {
    return [{ label: document.documentName, href: generatedLinkMap[document.documentName] }];
  }

  return [];
}

export default function OnboardingDetailPage() {
  const { employeeId: routeEmployeeId } = useParams();
  const authContext = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [reviewModal, setReviewModal] = useState({ open: false, document: null, status: 'approved' });
  const [savingReview, setSavingReview] = useState(false);
  const [statusDraft, setStatusDraft] = useState('in_progress');
  const [savingStatus, setSavingStatus] = useState(false);
  const [employeeProfile, setEmployeeProfile] = useState(null);
  const [workPreferences, setWorkPreferences] = useState(null);
  const [autoSyncingStepId, setAutoSyncingStepId] = useState('');
  const [generatedLinkMap, setGeneratedLinkMap] = useState({});
  const [fileList, setFileList] = useState([]);
  const [uploadForm] = Form.useForm();
  const [reviewForm] = Form.useForm();

  const token = authContext.token;
  const companyId = authContext.additionalUserInfo?.companyId;
  const currentEmployeeId = getCurrentEmployeeId(authContext);
  const employeeId = routeEmployeeId || currentEmployeeId;
  const isManager = getIsManager(authContext.role);
  const canManageEmployee = isManager || employeeId === currentEmployeeId;

  const groupedSteps = useMemo(() => {
    const steps = data?.checklistSteps || [];
    return steps.reduce((accumulator, step) => {
      const key = step.stepCategory || 'general';
      if (!accumulator[key]) {
        accumulator[key] = [];
      }
      accumulator[key].push(step);
      return accumulator;
    }, {});
  }, [data]);

  const pendingDocuments = useMemo(() => {
    return (data?.documents || []).filter((document) => document.status === 'uploaded' || document.status === 'under_review').length;
  }, [data]);

  const fetchData = useCallback(async () => {
    if (!token || !employeeId) return;
    setLoading(true);
    setError('');
    setNotFound(false);
    try {
      const [result, employees, preferences] = await Promise.all([
        fetchOnboardingStatus(token, employeeId),
        companyId ? FetchAllEmployeesByCompany(token).catch(() => []) : Promise.resolve([]),
        fetchEmployeeWorkPreferences(token, employeeId).catch(() => null),
      ]);

      if (!result) {
        setNotFound(true);
        setData(null);
        return;
      }

      const matchedEmployee = Array.isArray(employees)
        ? employees.find((employee) => (employee.id || employee.userId || employee.employeeId) === employeeId)
        : null;

      setData(result);
      setStatusDraft(result.status || 'in_progress');
      setEmployeeProfile(matchedEmployee || (currentEmployeeId === employeeId ? authContext.additionalUserInfo : null));
      setWorkPreferences(preferences);
    } catch (fetchError) {
      console.error(fetchError);
      setError('Failed to load onboarding record.');
    } finally {
      setLoading(false);
    }
  }, [token, employeeId, companyId, currentEmployeeId, authContext.additionalUserInfo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const autoSyncCandidates = [
      {
        step: data?.checklistSteps?.find((item) => item.stepKey === 'profile_basic_info'),
        isCompleteFromProfile: hasBasicProfileDetails(employeeProfile),
        notes: 'Auto-completed from profile basic information.',
      },
      {
        step: data?.checklistSteps?.find((item) => item.stepKey === 'bank_details'),
        isCompleteFromProfile: hasBankDetails(employeeProfile),
        notes: 'Auto-completed from profile bank details.',
      },
      {
        step: data?.checklistSteps?.find((item) => item.stepKey === 'tax_details'),
        isCompleteFromProfile: hasTaxDetails(employeeProfile),
        notes: 'Auto-completed from profile tax details.',
      },
      {
        step: data?.checklistSteps?.find((item) => item.stepKey === 'superannuation'),
        isCompleteFromProfile: hasSuperannuationDetails(employeeProfile),
        notes: 'Auto-completed from profile superannuation details.',
      },
      {
        step: data?.checklistSteps?.find((item) => item.stepKey === 'employment_conditions'),
        isCompleteFromProfile: hasEmploymentConditions(employeeProfile),
        notes: 'Auto-completed from employment conditions.',
      },
      {
        step: data?.checklistSteps?.find((item) => item.stepKey === 'work_preferences'),
        isCompleteFromProfile: hasWorkPreferences(workPreferences),
        notes: 'Auto-completed from work preferences.',
      },
    ];

    const stepToSync = autoSyncCandidates.find(({ step, isCompleteFromProfile }) => {
      return step && step.status !== 'completed' && isCompleteFromProfile && autoSyncingStepId !== step.stepId;
    });

    if (!stepToSync) {
      return;
    }

    setAutoSyncingStepId(stepToSync.step.stepId);
    updateChecklistStep(token, stepToSync.step.stepId, {
      status: 'completed',
      completedByEmployeeId: currentEmployeeId,
      completedDate: new Date().toISOString(),
      notes: stepToSync.notes,
    })
      .then(() => {
        message.success(`${stepToSync.step.stepLabel} updated from profile data`);
        fetchData();
      })
      .catch((syncError) => {
        console.error(syncError);
      })
      .finally(() => {
        setAutoSyncingStepId('');
      });
  }, [authContext.additionalUserInfo, autoSyncingStepId, currentEmployeeId, data, employeeProfile, token, workPreferences]);

  useEffect(() => {
    if (!data?.documents) return;

    const resolveLinks = async () => {
      let changed = false;
      const updatedDocuments = await Promise.all(
        data.documents.map(async (document) => {
          try {
            const presigned = await requestPresignedUrl(token, {
              employeeId,
              companyId,
              documentType: document.documentType,
              documentName: document.documentImages?.[0]?.keyName,
              documentImages: document.documentImages,
              contentType: 'application/octet-stream',
              fileSize: 0,
            });

            if (presigned?.presignedUrl) {
              changed = true;
              return {
                ...document,
                documentImages: document.documentImages?.map((img, i) =>
                  i === 0 ? { ...img, presignedUrl: presigned.presignedUrl } : img
                ),
              };
            }
          } catch (linkError) {
            console.error('Failed to resolve file link for', document.documentName, linkError);
          }
          return document;
        })
      );

      if (changed) {
        setData((previous) => ({ ...previous, documents: updatedDocuments }));
      }
    };

    resolveLinks();
  }, [companyId, data, employeeId, token]);

  const saveChecklistStep = async (stepId, payload) => {
    await updateChecklistStep(token, stepId, payload);
  };

  const handleQuickStepComplete = async (step) => {
    try {
      await saveChecklistStep(step.stepId, {
        status: step.status === 'completed' ? 'pending' : 'completed',
        completedByEmployeeId: currentEmployeeId,
        completedDate: new Date().toISOString(),
      });
      message.success('Checklist updated');
      fetchData();
    } catch (updateError) {
      console.error(updateError);
      message.error('Failed to update checklist step');
    }
  };

  const handleWelcomeSeen = async () => {
    try {
      await markWelcomeSeen(token, employeeId);
      message.success('Welcome state updated');
      fetchData();
    } catch (updateError) {
      console.error(updateError);
      message.error('Failed to update welcome state');
    }
  };

  const handleUpload = async () => {
    try {
      const values = await uploadForm.validateFields();
      const rawFile = fileList[0]?.originFileObj || fileList[0];
      if (!rawFile) {
        message.error('Attach a file first');
        return;
      }

      setUploading(true);
      await confirmDocumentUpload(token, {
        employeeId,
        companyId,
        documentType: values.documentType,
        documentName: values.documentName,
        description: values.description,
        s3Key: '',
        s3Bucket: '',
        contentType: rawFile.type || 'application/octet-stream',
        fileSize: rawFile.size,
        required: values.required,
        expiryDate: values.expiryDate || null,
        files: [rawFile],
      });

      message.success('Document uploaded');
      setUploadModalOpen(false);
      setFileList([]);
      uploadForm.resetFields();
      fetchData();
    } catch (uploadError) {
      if (uploadError?.errorFields) return;
      console.error(uploadError);
      message.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const openReviewModal = (document, status) => {
    setReviewModal({ open: true, document, status });
    reviewForm.setFieldsValue({
      managerNotes: '',
      rejectionReason: status === 'rejected' ? 'Please re-upload a clear and valid document.' : '',
    });
  };

  const handleReview = async () => {
    try {
      const values = await reviewForm.validateFields();
      setSavingReview(true);
      await reviewDocument(token, reviewModal.document.documentId, {
        status: reviewModal.status,
        reviewedByManagerId: currentEmployeeId,
        managerNotes: values.managerNotes,
        rejectionReason: values.rejectionReason,
      });

      message.success(`Document ${reviewModal.status}`);
      setReviewModal({ open: false, document: null, status: 'approved' });
      reviewForm.resetFields();
      fetchData();
    } catch (reviewError) {
      if (reviewError?.errorFields) return;
      console.error(reviewError);
      message.error('Failed to review document');
    } finally {
      setSavingReview(false);
    }
  };

  const handleSaveStatus = async () => {
    try {
      setSavingStatus(true);
      await updateOnboardingStatus(token, employeeId, {
        status: statusDraft,
        approvedByManagerId: currentEmployeeId,
        completedDate: statusDraft === 'completed' ? new Date().toISOString() : null,
      });
      message.success('Onboarding status updated');
      fetchData();
    } catch (statusError) {
      console.error(statusError);
      message.error('Failed to update onboarding status');
    } finally {
      setSavingStatus(false);
    }
  };

  const renderStepCard = ([category, steps]) => {
    const completed = steps.filter((step) => step.status === 'completed').length;
    const percent = steps.length ? Math.round((completed * 100) / steps.length) : 0;

    return (
      <Card key={category} className="onboarding-panel" title={category.replace(/_/g, ' ')}>
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          <Progress percent={percent} size="small" />
          <List
            dataSource={[...steps].sort((left, right) => (left.sortOrder || 0) - (right.sortOrder || 0))}
            renderItem={(step) => (
              <List.Item
                actions={[
                  canManageEmployee ? (
                    <Button size="small" loading={autoSyncingStepId === step.stepId} onClick={() => handleQuickStepComplete(step)}>
                      {step.status === 'completed' ? 'Re-open' : 'Mark complete'}
                    </Button>
                  ) : null,
                ].filter(Boolean)}
              >
                <List.Item.Meta
                  avatar={step.status === 'completed' ? <CheckCircleOutlined style={{ color: '#16a34a' }} /> : <ClockCircleOutlined style={{ color: '#f59e0b' }} />}
                  title={
                    <Space>
                      <span>{step.stepLabel}</span>
                      <Tag color={statusColor[step.status] || 'default'}>{step.status}</Tag>
                      {step.required ? <Tag>Required</Tag> : null}
                    </Space>
                  }
                  description={step.notes || step.stepKey}
                />
              </List.Item>
            )}
          />
        </Space>
      </Card>
    );
  };

  if (!employeeId) {
    return (
      <div className="onboarding-page">
        <Alert type="warning" showIcon message="Could not determine employee for onboarding." />
      </div>
    );
  }

  return (
    <div className="onboarding-page">
      <Space direction="vertical" size={20} style={{ width: '100%' }}>
        <div className="onboarding-hero">
          <div>
            <Title level={3} style={{ marginBottom: 8 }}>Onboarding Journey</Title>
            <Text type="secondary">
              Keep progress visible, move blockers quickly, and funnel employees toward a clean manager approval.
            </Text>
          </div>
          <Space>
            {isManager ? <Button onClick={() => navigate('/onboarding')}>Hub</Button> : null}
            <Button icon={<RobotOutlined />} onClick={() => navigate('/kbchatview')}>Meet Oscar</Button>
            <Button icon={<ReloadOutlined />} onClick={fetchData}>Refresh</Button>
          </Space>
        </div>

        {error && <Alert type="error" showIcon message={error} />}

        {notFound ? (
          <Card className="onboarding-panel">
            <Empty description="No onboarding record found for this employee" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </Card>
        ) : null}

        {data ? (
          <>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12} xl={6}><Card><Statistic title="Overall Progress" value={data.percentComplete || 0} suffix="%" /></Card></Col>
              <Col xs={24} md={12} xl={6}><Card><Statistic title="Completed Steps" value={data.completedSteps || 0} suffix={`/ ${data.totalSteps || 0}`} /></Card></Col>
              <Col xs={24} md={12} xl={6}><Card><Statistic title="Pending Documents" value={pendingDocuments} /></Card></Col>
              <Col xs={24} md={12} xl={6}><Card><Statistic title="Status" value={data.status || '—'} /></Card></Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col xs={24} xl={16}>
                <Space direction="vertical" style={{ width: '100%' }} size={16}>
                  <OnboardingWelcomeCard
                    welcomeSeen={data.welcomeMessageSeen}
                    oscarEnabled={data.oscarChatEnabled}
                    onMarkSeen={handleWelcomeSeen}
                    onOpenOscar={() => navigate('/kbchatview')}
                    canManageEmployee={canManageEmployee}
                  />
                  <Card className="onboarding-panel" title="Overview">
                    <Descriptions column={{ xs: 1, md: 2 }}>
                      <Descriptions.Item label="Employee ID">{data.employeeId || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Company ID">{data.companyId || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Started">{data.startedDate || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Completed">{data.completedDate || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Welcome seen">
                        <Tag color={data.welcomeMessageSeen ? 'success' : 'default'}>
                          {data.welcomeMessageSeen ? 'Seen' : 'Not yet'}
                        </Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label="Oscar">
                        <Tag color={data.oscarChatEnabled ? 'processing' : 'default'}>
                          {data.oscarChatEnabled ? 'Enabled' : 'Disabled'}
                        </Tag>
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                </Space>
              </Col>
              <Col xs={24} xl={8}>
                {isManager ? (
                  <OnboardingManagerActions
                    status={statusDraft}
                    updating={savingStatus}
                    onStatusChange={setStatusDraft}
                    onSaveStatus={handleSaveStatus}
                    onOpenUpload={() => setUploadModalOpen(true)}
                  />
                ) : (
                  <Card className="onboarding-panel onboarding-accent-panel" title="My Actions">
                    <Paragraph>
                      Upload missing paperwork and use Oscar whenever a step or policy is unclear.
                    </Paragraph>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Button type="primary" block icon={<UploadOutlined />} onClick={() => setUploadModalOpen(true)}>
                        Upload document
                      </Button>
                      <Button block icon={<RobotOutlined />} onClick={() => navigate('/kbchatview')}>
                        Ask Oscar
                      </Button>
                    </Space>
                  </Card>
                )}
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col xs={24} xl={14}>
                <Space direction="vertical" style={{ width: '100%' }} size={16}>
                  {Object.entries(groupedSteps).map(renderStepCard)}
                </Space>
              </Col>
              <Col xs={24} xl={10}>
                <Card className="onboarding-panel" title="Documents" extra={<Button type="link" onClick={() => setUploadModalOpen(true)}>Upload</Button>}>
                  {(data.documents || []).length === 0 ? (
                    <Empty description="No documents uploaded yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  ) : (
                    <List
                      dataSource={data.documents}
                      renderItem={(document) => (
                        <List.Item
                          actions={isManager ? [
                            <Button size="small" key="approve" onClick={() => openReviewModal(document, 'approved')}>Approve</Button>,
                            <Button size="small" danger key="reject" onClick={() => openReviewModal(document, 'rejected')}>Reject</Button>,
                          ] : []}
                        >
                          <List.Item.Meta
                            title={
                              <Space>
                                <span>{document.documentName}</span>
                                <Tag color={statusColor[document.status] || 'default'}>{document.status}</Tag>
                              </Space>
                            }
                            description={
                              <Space direction="vertical" size={2}>
                                <Text type="secondary">{document.documentType}</Text>
                                <Text type="secondary">{document.description || 'No description'}</Text>
                                {getDocumentLinks(document, generatedLinkMap).map((file, index) => (
                                  <a key={`${document.documentId || document.documentName}-file-${index}`} href={file.href} target="_blank" rel="noreferrer">
                                    {file.label}
                                  </a>
                                ))}
                                {document.rejectionReason ? <Text type="danger">{document.rejectionReason}</Text> : null}
                              </Space>
                            }
                          />
                        </List.Item>
                      )}
                    />
                  )}
                </Card>
              </Col>
            </Row>
          </>
        ) : null}
      </Space>

      <Modal
        open={uploadModalOpen}
        title="Upload onboarding document"
        onCancel={() => {
          setUploadModalOpen(false);
          setFileList([]);
        }}
        onOk={handleUpload}
        okText="Upload"
        okButtonProps={{ loading: uploading }}
        destroyOnClose
      >
        <Form form={uploadForm} layout="vertical" initialValues={{ required: true }}>
          <Form.Item name="documentType" label="Document type" rules={[{ required: true, message: 'Document type is required' }]}>
            <Select options={documentTypeOptions} />
          </Form.Item>
          <Form.Item name="documentName" label="Document name" rules={[{ required: true, message: 'Document name is required' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="expiryDate" label="Expiry date">
            <Input placeholder="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item name="required" label="Required" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label="Attachment" required>
            <Dragger
              fileList={fileList}
              beforeUpload={(file) => {
                setFileList([file]);
                uploadForm.setFieldValue('documentName', file.name);
                return false;
              }}
              onRemove={() => setFileList([])}
              maxCount={1}
            >
              <p className="ant-upload-drag-icon"><InboxOutlined /></p>
              <p className="ant-upload-text">Drop a file here or click to browse</p>
            </Dragger>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={reviewModal.open}
        title={reviewModal.status === 'approved' ? 'Approve document' : 'Reject document'}
        onCancel={() => setReviewModal({ open: false, document: null, status: 'approved' })}
        onOk={handleReview}
        okText={reviewModal.status === 'approved' ? 'Approve' : 'Reject'}
        okButtonProps={{ loading: savingReview, danger: reviewModal.status === 'rejected' }}
        destroyOnClose
      >
        <Form form={reviewForm} layout="vertical">
          <Form.Item name="managerNotes" label="Manager notes">
            <Input.TextArea rows={3} placeholder="Optional internal note" />
          </Form.Item>
          {reviewModal.status === 'rejected' ? (
            <Form.Item
              name="rejectionReason"
              label="Rejection reason"
              rules={[{ required: true, message: 'Rejection reason is required' }]}
            >
              <Input.TextArea rows={3} />
            </Form.Item>
          ) : null}
        </Form>
      </Modal>
    </div>
  );
}
